import { Card } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Inbox, CheckCircle2, Loader2, Flame, Timer, ListTodo, Trophy, ShieldCheck, Wrench, Wheat, CloudSun, Brain, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { PRIORITY_LABELS } from "@/lib/labels";

export const PIE_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4", "#f97316", "#84cc16", "#64748b"];
export const tooltipStyle = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  color: "hsl(var(--popover-foreground))",
};

export const tooltipLabelStyle = {
  color: "hsl(var(--popover-foreground))",
};

export const tooltipItemStyle = {
  color: "hsl(var(--popover-foreground))",
};

/**
 * Constroi os parâmetros de filtro de cliente para chamadas próprias
 * dos widgets, considerando o CONTEXTO GLOBAL:
 *
 * - filters.isAll            -> "todos os clientes" do membership
 * - filters.clientIds (>1)   -> múltiplos clientes (client_ids)
 * - filters.clientId (1)     -> cliente único
 * - filters.unitId / days    -> compatibilidade
 */
export function buildClientContextParams(filters, params = {}) {
  if (!filters) return params;
  if (filters.isAll) {
    params.is_all = "true";
  } else if (Array.isArray(filters.clientIds) && filters.clientIds.length > 1) {
    params.client_ids = filters.clientIds.join(",");
  } else if (filters.clientId && filters.clientId !== "todos") {
    params.client_id = filters.clientId;
  }
  if (filters.unitId && filters.unitId !== "todos") {
    params.unit_id = filters.unitId;
  }
  if (filters.days && filters.days !== "0") {
    params.days = filters.days;
  }
  return params;
}

export function ChartCard({ title, children, className = "", testId, extra }) {
  return (
    <Card className={`p-5 sm:p-6 border-border bg-card h-full ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="font-heading font-semibold text-sm tracking-tight">{title}</h3>
        {extra}
      </div>
      {children}
    </Card>
  );
}

const Empty = () => <p className="text-sm text-muted-foreground py-8 text-center">Sem dados no período.</p>;

const KPI = [
  { key: "aberto", label: "Em aberto", icon: Inbox, color: "text-blue-400" },
  { key: "concluido", label: "Concluídas", icon: CheckCircle2, color: "text-emerald-400" },
  { key: "andamento", label: "Em andamento", icon: Loader2, color: "text-amber-400" },
  { key: "aguardando_aprovacao", label: "Aguard. aprovação", icon: ShieldCheck, color: "text-cyan-400" },
  { key: "criticas", label: "Críticas", icon: Flame, color: "text-red-400" },
  { key: "tempo_medio_horas", label: "Tempo médio (h)", icon: Timer, color: "text-violet-400" },
  { key: "pendencias", label: "Pendências", icon: ListTodo, color: "text-orange-400" },
];

export function KpisWidget({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
      {KPI.map(({ key, label, icon: Icon, color }) => (
        <Card key={key} className="p-4 border-border bg-card" data-testid={`kpi-${key}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground font-medium">{label}</p>
            <Icon size={15} className={color} />
          </div>
          <p className="font-heading text-2xl sm:text-3xl font-bold mt-2 tracking-tight">{stats?.[key] ?? 0}</p>
        </Card>
      ))}
    </div>
  );
}

const RISK_META = [
  { key: "verde", label: "No prazo", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400", bar: "bg-emerald-500" },
  { key: "amarelo", label: "Atenção", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400", bar: "bg-amber-400" },
  { key: "laranja", label: "Crítico", cls: "border-orange-500/40 bg-orange-500/10 text-orange-400", bar: "bg-orange-500" },
  { key: "vermelho", label: "Estourado", cls: "border-red-500/40 bg-red-500/10 text-red-400", bar: "bg-red-500" },
];

export function RiskLevelsWidget({ bi }) {
  const nr = bi?.niveis_risco || {};
  const total = nr.total_abertas || 0;
  return (
    <Card className="p-5 sm:p-6 border-border bg-card" data-testid="risk-levels-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-sm tracking-tight">Níveis de Risco — ocorrências em aberto (semáforo SLA)</h3>
        <span className="text-xs text-muted-foreground shrink-0">{total} em aberto</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {RISK_META.map((r) => (
          <div key={r.key} className={`rounded-lg border p-4 ${r.cls}`} data-testid={`risk-level-${r.key}`}>
            <p className="font-heading text-3xl font-bold tracking-tight">{nr[r.key] ?? 0}</p>
            <p className="text-[11px] font-semibold mt-1 uppercase tracking-wider">{r.label}</p>
          </div>
        ))}
      </div>
      {total > 0 && (
        <div className="flex h-2 rounded-full overflow-hidden mt-4 bg-muted">
          {RISK_META.map((r) => (
            <div key={r.key} className={r.bar} style={{ width: `${((nr[r.key] || 0) / total) * 100}%` }} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function MonthWidget({ charts, filters }) {
  const [granularity, setGranularity] = useState("month");
  const [autoRotate, setAutoRotate] = useState(true);
  const [data, setData] = useState(charts?.por_tempo || charts?.por_mes || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadTemporalChart() {
      setLoading(true);
      setError("");

      const params = buildClientContextParams(filters, { granularity });

      try {
        const { data: response } = await api.get("/dashboard/charts", { params });
        if (cancelled) return;
        setData(response?.por_tempo || []);
      } catch (err) {
        if (cancelled) return;
        console.error("DASHBOARD TEMPORAL CHART ERROR", err);
        setError("Não foi possível atualizar este gráfico.");
        setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTemporalChart();
    return () => {
      cancelled = true;
    };
  }, [granularity, filters?.clientId, filters?.clientIds?.join(","), filters?.isAll, filters?.unitId, filters?.days]);

  // Rotação automática: começa em mês e alterna mês → dia → semana → mês.
  // O intervalo de 8 segundos deixa a leitura confortável sem transformar
  // o gráfico em um carrossel rápido demais.
  useEffect(() => {
    if (!autoRotate) return undefined;

    const sequence = ["month", "day", "week"];
    const timer = window.setInterval(() => {
      setGranularity((current) => {
        const index = sequence.indexOf(current);
        return sequence[(index + 1) % sequence.length];
      });
    }, 8000);

    return () => window.clearInterval(timer);
  }, [autoRotate]);

  const label = granularity === "day" ? "dia" : granularity === "week" ? "semana" : "mês";
  const title = `Ocorrências por ${label}`;

  const formattedData = (data || []).map((row, index) => ({
    ...row,
    name:
      granularity === "day"
        ? (() => {
            const [year, month, day] = String(row.name).split("-").map(Number);
            if (!year || !month || !day) return row.name;
            const date = new Date(Date.UTC(year, month - 1, day));
            const weekday = new Intl.DateTimeFormat("pt-BR", {
              weekday: "short",
              timeZone: "UTC",
            }).format(date).replace(".", "");
            const weekdayShort = weekday.charAt(0).toUpperCase() + weekday.slice(1);
            return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year} — ${weekdayShort}`;
          })()
        : granularity === "week"
          ? `Semana ${index + 1}`
          : (() => {
              const [year, month] = String(row.name).split("-").map(Number);
              if (!year || !month) return row.name;

              const monthName = new Intl.DateTimeFormat("pt-BR", {
                month: "long",
                timeZone: "UTC",
              }).format(new Date(Date.UTC(year, month - 1, 1)));

              const monthLabel = monthName.charAt(0).toUpperCase() + monthName.slice(1);
              return `${monthLabel} ${year}`;
            })(),
  }));

  return (
    <ChartCard
      title={title}
      testId="chart-by-time"
      extra={(
        <div className="flex items-center gap-2">
          {loading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-muted-foreground" aria-live="polite">
            {autoRotate ? "Automático · 8s" : "Manual"}
          </span>
          <button
            type="button"
            onClick={() => setAutoRotate((current) => !current)}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            aria-pressed={autoRotate}
            aria-label={autoRotate ? "Pausar rotação automática" : "Ativar rotação automática"}
            data-testid="dashboard-occurrence-auto-rotate"
          >
            {autoRotate ? "Pausar" : "Automático"}
          </button>
          <label className="sr-only" htmlFor="dashboard-occurrence-granularity">Agrupamento das ocorrências</label>
          <select
            id="dashboard-occurrence-granularity"
            value={granularity}
            onChange={(event) => {
              setGranularity(event.target.value);
              setAutoRotate(false);
            }}
            className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:ring-2 focus:ring-primary/30"
            data-testid="dashboard-occurrence-granularity"
          >
            <option value="day">Por dia</option>
            <option value="week">Por semana</option>
            <option value="month">Por mês</option>
          </select>
        </div>
      )}
    >
      {error ? (
        <p className="text-sm text-amber-500 py-8 text-center">{error}</p>
      ) : !formattedData.length ? (
        <Empty />
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="gradOccurrenceTime" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,10%,15%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={34} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} fill="url(#gradOccurrenceTime)" name="Ocorrências" />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

export function CategoryWidget({ charts }) {
  const data = charts?.por_categoria || [];
  return (
    <ChartCard title="Por categoria" testId="chart-by-category">
      {!data.length ? <Empty /> : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data} dataKey="total" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2} strokeWidth={0}>
                {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {data.slice(0, 6).map((c, i) => (
              <span key={`${c.name}-${i}`} className="text-[11px] text-muted-foreground flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{c.name}
              </span>
            ))}
          </div>
        </>
      )}
    </ChartCard>
  );
}

function HBar({ data, color, testId, title }) {
  return (
    <ChartCard title={title} testId={testId}>
      {!data?.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data} layout="vertical" margin={{ left: 8 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={110} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="total" fill={color} radius={[0, 4, 4, 0]} barSize={14} name="Ocorrências" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

export function UnitWidget({ charts }) {
  return <HBar data={charts?.por_unidade} color="#22c55e" testId="chart-by-unit" title="Por unidade" />;
}

export function AreaWidget({ charts }) {
  return <HBar data={charts?.por_area} color="#a855f7" testId="chart-by-area" title="Por área" />;
}

export function PestSpeciesWidget({ bi }) {
  const data = (bi?.pragas_tipo || bi?.pragas_especie || []).slice(0, 8);
  return <HBar data={data} color="#ef4444" testId="pest-type-widget" title="Pragas por tipo de ocorrência" />;
}

export function PestEvolutionWidget({ bi }) {
  const ev = bi?.pragas_evolucao || { tipos: [], especies: [], data: [] };
  const series = ev.tipos || ev.especies || [];
  return (
    <ChartCard title="Evolução de pragas por tipo de ocorrência" testId="pest-evolution-widget">
      {!ev.data?.length || !series.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={ev.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,10%,15%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((sp, i) => (
              <Line key={sp} type="monotone" dataKey={sp} stroke={PIE_COLORS[i % PIE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}


function RodentChartCard({ title, children, testId }) {
  return <ChartCard title={title} testId={testId}>{children}</ChartCard>;
}

export function RodentSpeciesWidget({ bi }) {
  const data = (bi?.roedores?.por_especie || []).slice(0, 8);
  return (
    <RodentChartCard title="Roedores por espécie" testId="rodent-species-widget">
      {!data.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 12 }}>
            <XAxis type="number" allowDecimals={false} hide />
            <YAxis type="category" dataKey="name" width={115} tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} formatter={(value, name, item) => [value, "Roedores"]} labelFormatter={(label) => {
              const row = data.find((x) => x.name === label);
              return row?.scientific_name ? `${label} — ${row.scientific_name}` : label;
            }} />
            <Bar dataKey="total" fill="#ef4444" radius={[0, 4, 4, 0]} barSize={16} name="Roedores" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </RodentChartCard>
  );
}

export function RodentEvolutionWidget({ bi }) {
  const ev = bi?.roedores?.evolucao_por_especie || { especies: [], data: [] };
  return (
    <ChartCard title="Evolução de roedores por espécie" testId="rodent-evolution-widget">
      {!ev.data?.length || !ev.especies?.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={ev.data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,10%,15%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {ev.especies.map((sp, i) => <Line key={sp} type="monotone" dataKey={sp} stroke={PIE_COLORS[i % PIE_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />)}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

function RodentLocationBar({ title, data, testId }) {
  const rows = (data || []).slice(0, 10);
  return (
    <ChartCard title={title} testId={testId}>
      {!rows.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 12 }}>
            <XAxis type="number" allowDecimals={false} hide />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="total" fill="#f97316" radius={[0, 4, 4, 0]} barSize={15} name="Roedores" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

export function RodentUnitWidget({ bi }) {
  return <RodentLocationBar data={bi?.roedores?.por_unidade} title="Roedores por unidade" testId="rodent-unit-widget" />;
}

export function RodentAreaWidget({ bi }) {
  return <RodentLocationBar data={bi?.roedores?.por_area} title="Roedores por área" testId="rodent-area-widget" />;
}

export function RodentSectorWidget({ bi }) {
  return <RodentLocationBar data={bi?.roedores?.por_setor} title="Roedores por setor / sala" testId="rodent-sector-widget" />;
}

export function RodentSituationWidget({ bi }) {
  const data = bi?.roedores?.por_situacao || [];
  return (
    <ChartCard title="Situação dos registros de roedores" testId="rodent-situation-widget">
      {!data.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={230}>
          <PieChart>
            <Pie
              data={data}
              dataKey="total"
              nameKey="name"
              innerRadius={48}
              outerRadius={82}
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

export function RodentQuantityWidget({ bi }) {
  const data = bi?.roedores?.por_quantidade || [];
  const summary = bi?.roedores?.resumo || {};
  return (
    <ChartCard title="Roedores por quantidade registrada" testId="rodent-quantity-widget"
      extra={<span className="text-[11px] text-muted-foreground">média {summary.media_por_evento ?? 0}/evento</span>}>
      {!data.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={230}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,10%,15%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={30} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="eventos" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={24} name="Eventos" />
            <Bar dataKey="total" fill="#06b6d4" radius={[4, 4, 0, 0]} barSize={24} name="Roedores" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

const AREA_RISK_CLS = {
  verde: "bg-emerald-500/15 text-emerald-400",
  amarelo: "bg-amber-500/15 text-amber-400",
  laranja: "bg-orange-500/15 text-orange-400",
  vermelho: "bg-red-500/15 text-red-400",
};
const AREA_RISK_BAR = { verde: "bg-emerald-500", amarelo: "bg-amber-400", laranja: "bg-orange-500", vermelho: "bg-red-500" };

export function CriticalAreasWidget({ bi }) {
  const areas = (bi?.areas_criticas || []).slice(0, 6);
  const max = areas[0]?.score || 1;
  return (
    <ChartCard title="Áreas críticas — mapa de calor" testId="critical-areas-widget">
      {!areas.length ? <Empty /> : (
        <div className="space-y-3">
          {areas.map((a, i) => (
            <div key={`${a.name}-${i}`} className="flex items-center gap-3" data-testid={`critical-area-${i}`}>
              <div className={`h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${AREA_RISK_CLS[a.risco]}`}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{a.name}</p>
                  <span className="text-[10px] text-muted-foreground truncate">{a.unit_name}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted mt-1">
                  <div className={`h-full rounded-full ${AREA_RISK_BAR[a.risco]}`} style={{ width: `${Math.min(100, (a.score / max) * 100)}%` }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold">{a.score}</p>
                <p className="text-[10px] text-muted-foreground">{a.criticas} crít · {a.reincidencias} reinc</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function AstralOpsWidget({ bi }) {
  const op = bi?.operacional || { total: 0, abertas: 0, por_tipo: [] };
  const data = (op.por_tipo || []).slice(0, 6);
  return (
    <ChartCard title="Operacional Astral" testId="astral-ops-widget"
      extra={<Wrench size={14} className="text-fuchsia-400" />}>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 p-3">
          <p className="font-heading text-2xl font-bold text-fuchsia-400">{op.total}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Registros</p>
        </div>
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3">
          <p className="font-heading text-2xl font-bold text-orange-400">{op.abertas}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Em aberto</p>
        </div>
      </div>
      {!data.length ? <p className="text-xs text-muted-foreground text-center py-4">Sem não conformidades Astral no período.</p> : (
        <div className="space-y-2">
          {data.map((t, i) => (
            <div key={`${t.name}-${i}`} className="flex items-center gap-2">
              <p className="text-xs flex-1 truncate">{t.name}</p>
              <div className="h-1.5 w-20 rounded-full bg-muted shrink-0">
                <div className="h-full rounded-full bg-fuchsia-500" style={{ width: `${(t.total / (data[0]?.total || 1)) * 100}%` }} />
              </div>
              <span className="text-xs font-semibold w-6 text-right">{t.total}</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

const HEALTH_CLS = { verde: "text-emerald-400", amarelo: "text-amber-400", laranja: "text-orange-400", vermelho: "text-red-400" };
const HEALTH_BAR = { verde: "bg-emerald-500", amarelo: "bg-amber-400", laranja: "bg-orange-500", vermelho: "bg-red-500" };

export function CapturesWidget({ bi }) {
  const cap = bi?.capturas || {};
  const meta = [
    ["Capturados", cap.total ?? 0, "text-rose-400"],
    ["Vivos", cap.vivos ?? 0, "text-amber-400"],
    ["Mortos", cap.mortos ?? 0, "text-slate-300"],
    ["Salas sem captura", `${cap.pct_sem_captura ?? 100}%`, "text-emerald-400"],
  ];
  return (
    <ChartCard title="Capturas de Roedores — Salas Elétricas" testId="captures-widget"
      extra={cap.dias_desde_ultima != null ? <span className="text-[11px] text-muted-foreground shrink-0">última há {cap.dias_desde_ultima}d</span> : null}>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {meta.map(([l, v, cls]) => (
          <div key={l} className="rounded-lg border border-border p-2.5 text-center">
            <p className={`font-heading text-xl font-bold ${cls}`}>{v}</p>
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{l}</p>
          </div>
        ))}
      </div>
      {cap.por_mes?.length ? (
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={cap.por_mes}>
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={26} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="total" fill="#f43f5e" radius={[4, 4, 0, 0]} barSize={20} name="Roedores" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-4">Nenhuma captura registrada no período.</p>
      )}
      {cap.por_especie?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {cap.por_especie.slice(0, 5).map((s, i) => (
            <span key={`${s.name}-${i}`} className="text-[11px] px-2 py-0.5 rounded-full border border-rose-500/30 bg-rose-500/10 text-rose-300">{s.name}: {s.total}</span>
          ))}
        </div>
      )}
      {(cap.fezes_total > 0 || cap.vestigios_total > 0) && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="bio-evidence-chips">
          {cap.fezes_total > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300">Fezes (registros): {cap.fezes_total}</span>}
          {cap.vestigios_total > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-300">Vestígios: {cap.vestigios_total}</span>}
        </div>
      )}
    </ChartCard>
  );
}

export function RoomsHealthWidget({ bi }) {
  const salas = bi?.salas_saude || [];
  return (
    <ChartCard title="Saúde das Salas Elétricas" testId="rooms-health-widget">
      {!salas.length ? <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma sala elétrica cadastrada.</p> : (
        <div className="space-y-3">
          {salas.slice(0, 7).map((s, i) => (
            <div key={`${s.name}-${i}`} className="flex items-center gap-3" data-testid={`room-health-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{s.name}</p>
                  <span className="text-[10px] text-muted-foreground truncate">{s.unit_name}</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted mt-1">
                  <div className={`h-full rounded-full ${HEALTH_BAR[s.band]}`} style={{ width: `${s.score}%` }} />
                </div>
              </div>
              <span className={`font-heading text-sm font-bold ${HEALTH_CLS[s.band]}`}>{s.score}</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function PriorityWidget({ charts }) {
  const data = charts?.por_prioridade || [];
  return (
    <ChartCard title="Por prioridade" testId="chart-by-priority">
      {!data.length ? <Empty /> : (
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.map((p) => ({ ...p, name: PRIORITY_LABELS[p.name] || p.name }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(240,10%,15%)" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="total" radius={[4, 4, 0, 0]} barSize={32} name="Ocorrências">
              {data.map((p, i) => (
                <Cell key={i} fill={{ baixa: "#64748b", media: "#f59e0b", alta: "#f97316", critica: "#ef4444" }[p.name] || "#3b82f6"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

export function RankingWidget({ charts }) {
  const ranking = charts?.ranking || [];
  return (
    <ChartCard title="Ranking de unidades" testId="ranking-card">
      {!ranking.length ? <Empty /> : (
        <div className="space-y-3">
          {ranking.map((r, i) => (
            <div key={`${r.name}-${i}`} className="flex items-center gap-3">
              <div className={`h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? "bg-amber-500/20 text-amber-400" : "bg-muted text-muted-foreground"}`}>
                {i === 0 ? <Trophy size={13} /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{r.name}</p>
                <div className="h-1.5 rounded-full bg-muted mt-1">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${(r.total / (ranking[0]?.total || 1)) * 100}%` }} />
                </div>
              </div>
              <span className="text-sm font-semibold">{r.total}</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}

export function SlaWidget({ charts, bi }) {
  const sla = charts?.sla || [];
  const nr = bi?.niveis_risco || {};

  // Resumo agregado de SLA a partir dos dados reais do backend.
  // charts.sla traz total/concluídas por cliente; niveis_risco traz o
  // semáforo das ocorrências abertas (verde=dentro, demais=fora/risco).
  const slaTotal = sla.reduce((acc, s) => acc + (s.total || 0), 0);
  const slaConcluidas = sla.reduce((acc, s) => acc + (s.concluidas || 0), 0);
  const slaFora = nr.vermelho || 0;
  const slaIndiceReal = slaTotal
    ? Math.round((slaConcluidas / slaTotal) * 100)
    : 0;

  return (
    <ChartCard title="SLA — resumo por cliente" testId="sla-card">
      {!sla.length ? <Empty /> : (
        <>
          <div className="rounded-lg border border-border bg-muted/20 p-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sla-resumo">
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
              <p className="font-heading text-xl font-bold mt-0.5">{slaTotal}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wider text-emerald-500">Dentro do prazo</p>
              <p className="font-heading text-xl font-bold mt-0.5 text-emerald-500">{slaConcluidas}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wider text-red-500">Fora do prazo</p>
              <p className="font-heading text-xl font-bold mt-0.5 text-red-500">{slaFora}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Índice SLA</p>
              <p className="font-heading text-xl font-bold mt-0.5">{slaIndiceReal}%</p>
            </div>
          </div>

          <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="pb-2 font-medium">Cliente</th>
                <th className="pb-2 font-medium">Total</th>
                <th className="pb-2 font-medium">Concluídas</th>
                <th className="pb-2 font-medium">Tempo médio</th>
                <th className="pb-2 font-medium w-40">SLA</th>
              </tr>
            </thead>
            <tbody>
              {sla.map((s, i) => (
                <tr key={`${s.name}-${i}`} className="border-b border-border/50 last:border-0">
                  <td className="py-2.5 font-medium">{s.name}</td>
                  <td className="py-2.5">{s.total}</td>
                  <td className="py-2.5">{s.concluidas}</td>
                  <td className="py-2.5">{s.tempo_medio_h}h</td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-muted">
                        <div className={`h-full rounded-full ${s.sla_pct >= 70 ? "bg-emerald-500" : s.sla_pct >= 40 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${s.sla_pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold w-10">{s.sla_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </ChartCard>
  );
}

const PGA_LEVELS = [
  ["baixo", "Baixo", "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"],
  ["medio", "Médio", "border-amber-500/40 bg-amber-500/10 text-amber-400"],
  ["alto", "Alto", "border-orange-500/40 bg-orange-500/10 text-orange-400"],
  ["critico", "Crítico", "border-red-500/40 bg-red-500/10 text-red-400"],
];

export function PgaWidget({ bi }) {
  const p = bi?.pga;
  return (
    <ChartCard title="Pragas em Grãos Armazenados (PGA)" testId="pga-widget"
      extra={<Wheat size={14} className="text-yellow-500" />}>
      {!p ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Nenhum cliente com o módulo PGA habilitado nos filtros atuais.</p>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-2 mb-4">
            <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-2.5 text-center" data-testid="pga-widget-media">
              <p className="font-heading text-xl font-bold text-yellow-500">{p.media_indice}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">Índice médio</p>
            </div>
            {PGA_LEVELS.map(([k, l, cls]) => (
              <div key={k} className={`rounded-lg border p-2.5 text-center ${cls}`} data-testid={`pga-widget-${k}`}>
                <p className="font-heading text-xl font-bold">{p.counts?.[k] ?? 0}</p>
                <p className="text-[9px] uppercase tracking-wider mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          {p.evolucao_mensal?.length ? (
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={p.evolucao_mensal}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={26} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="total" fill="#eab308" radius={[4, 4, 0, 0]} barSize={20} name="Capturas" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">Nenhuma captura registrada no período.</p>
          )}
          {p.por_especie?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {p.por_especie.slice(0, 5).map((s, i) => (
                <span key={`${s.name}-${i}`} className="text-[11px] px-2 py-0.5 rounded-full border border-yellow-500/30 bg-yellow-500/10 text-yellow-500">{s.name}: {s.total}</span>
              ))}
            </div>
          )}
          {p.criticos?.length > 0 && (
            <div className="space-y-1.5 mt-3">
              {p.criticos.slice(0, 4).map((c, i) => (
                <div key={`${c.name}-${i}`} className="flex items-center justify-between gap-2 text-xs">
                  <p className="truncate">{c.name} <span className="text-muted-foreground">({c.type_name})</span></p>
                  <span className={`font-semibold shrink-0 ${c.level === "critico" ? "text-red-400" : "text-orange-400"}`}>{c.index}/100</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </ChartCard>
  );
}

const IRO = {
  verde: ["Baixo", "text-emerald-400", "bg-emerald-500"],
  amarelo: ["Moderado", "text-amber-400", "bg-amber-500"],
  laranja: ["Alto", "text-orange-400", "bg-orange-500"],
  vermelho: ["Crítico", "text-red-400", "bg-red-500"],
};

export function RiskOpWidget({ filters }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = buildClientContextParams(filters, {});

    setData(null);

    api.get("/environment/risk", { params })
      .then(({ data: d }) => setData(d))
      .catch(() => setData({ units: [], summary: null }));
  }, [filters?.clientId, filters?.clientIds?.join(","), filters?.isAll, filters?.unitId]);
  const s = data?.summary;
  return (
    <ChartCard title="Índice de Risco Operacional — Inteligência Ambiental" testId="risk-op-widget"
      extra={<CloudSun size={14} className="text-sky-400" />}>
      {!data ? (
        <div className="py-10 flex justify-center"><Loader2 size={20} className="animate-spin text-muted-foreground" /></div>
      ) : !data.units?.length ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma unidade no filtro atual.</p>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-2 mb-4">
            <div className="rounded-lg border border-sky-500/40 bg-sky-500/10 p-2.5 text-center" data-testid="risk-op-media">
              <p className={`font-heading text-xl font-bold ${IRO[s.level]?.[1] || ""}`}>{s.media}</p>
              <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">Risco médio</p>
            </div>
            {Object.entries(IRO).map(([k, [l, txt]]) => (
              <div key={k} className="rounded-lg border border-border p-2.5 text-center" data-testid={`risk-op-${k}`}>
                <p className={`font-heading text-xl font-bold ${txt}`}>{s.counts?.[k] ?? 0}</p>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {data.units.slice(0, 5).map((u) => (
              <div key={u.unit_id} className="flex items-center gap-2" data-testid={`risk-op-unit-${u.unit_id}`}>
                <p className="text-xs w-40 truncate">{u.unit_name} <span className="text-muted-foreground">· {u.client_name}</span></p>
                <div className="h-1.5 flex-1 rounded-full bg-muted">
                  <div className={`h-full rounded-full ${IRO[u.level]?.[2] || "bg-slate-500"}`} style={{ width: `${u.iro}%` }} />
                </div>
                <span className={`text-xs font-semibold w-8 text-right ${IRO[u.level]?.[1] || ""}`}>{u.iro}</span>
              </div>
            ))}
          </div>
          {s.alertas > 0 && (
            <p className="text-[11px] text-amber-400 mt-3 flex items-center gap-1.5">
              <Flame size={11} /> {s.alertas} alerta(s) ambiental(is) ativo(s) — detalhes em Inteligência Ambiental
            </p>
          )}
        </>
      )}
    </ChartCard>
  );
}


function AnalysisSignal({ icon: Icon, title, text, tone = "neutral" }) {
  const toneMap = {
    positive: "border-emerald-500/30 bg-emerald-500/10",
    negative: "border-red-500/30 bg-red-500/10",
    attention: "border-amber-500/30 bg-amber-500/10",
    neutral: "border-border bg-muted/20",
  };
  const iconMap = {
    positive: "text-emerald-400",
    negative: "text-red-400",
    attention: "text-amber-400",
    neutral: "text-sky-400",
  };
  return (
    <div className={`rounded-xl border p-3 ${toneMap[tone] || toneMap.neutral}`}>
      <div className="flex items-start gap-2.5">
        <Icon size={16} className={`mt-0.5 shrink-0 ${iconMap[tone] || iconMap.neutral}`} />
        <div className="min-w-0">
          <p className="text-xs font-semibold">{title}</p>
          <p className="text-[11px] leading-relaxed text-muted-foreground mt-1">{text}</p>
        </div>
      </div>
    </div>
  );
}

export function IntelligentAnalysisWidget({ bi }) {
  const a = bi?.analise_inteligente || {};
  const resumo = a.resumo || {};
  const sinais = a.sinais || [];
  const positivos = a.positivos || [];
  const fatores = a.fatores || [];

  return (
    <ChartCard
      title="Análise Inteligente"
      testId="intelligent-analysis-widget"
      extra={<Brain size={15} className="text-sky-400" />}
    >
      <div className="space-y-3">
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-4">
          <p className="text-sm font-semibold">{resumo.titulo || "Leitura inteligente do período"}</p>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
            {resumo.texto || "Ainda não há volume de dados suficiente para uma interpretação mais específica."}
          </p>
          {resumo.variacao_pct != null && (
            <div className="flex items-center gap-2 mt-3">
              {resumo.variacao_pct > 0 ? (
                <TrendingUp size={15} className="text-red-400" />
              ) : resumo.variacao_pct < 0 ? (
                <TrendingDown size={15} className="text-emerald-400" />
              ) : null}
              <span className="text-xs font-semibold">
                {resumo.variacao_pct > 0 ? "+" : ""}{resumo.variacao_pct}% versus período anterior
              </span>
            </div>
          )}
        </div>

        {fatores.length > 0 && (
          <div>
            <p className="text-xs font-semibold mb-2">Fatores associados</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {fatores.slice(0, 4).map((f, i) => (
                <AnalysisSignal
                  key={`${f.nome || f.name || "fator"}-${i}`}
                  icon={AlertTriangle}
                  title={f.nome || f.name || "Fator"}
                  text={f.texto || f.description || "Associação identificada nos dados operacionais."}
                  tone={f.tone || "attention"}
                />
              ))}
            </div>
          </div>
        )}

        {sinais.length > 0 && (
          <div>
            <p className="text-xs font-semibold mb-2">Sinais de atenção</p>
            <div className="space-y-2">
              {sinais.slice(0, 3).map((s, i) => (
                <AnalysisSignal
                  key={`${s.titulo || "sinal"}-${i}`}
                  icon={AlertTriangle}
                  title={s.titulo || "Sinal identificado"}
                  text={s.texto || ""}
                  tone="negative"
                />
              ))}
            </div>
          </div>
        )}

        {positivos.length > 0 && (
          <div>
            <p className="text-xs font-semibold mb-2">Correlação positiva</p>
            <div className="space-y-2">
              {positivos.slice(0, 3).map((p, i) => (
                <AnalysisSignal
                  key={`${p.titulo || "positivo"}-${i}`}
                  icon={TrendingDown}
                  title={p.titulo || "Efeito positivo identificado"}
                  text={p.texto || ""}
                  tone="positive"
                />
              ))}
            </div>
          </div>
        )}

        <p className="text-[10px] text-muted-foreground leading-relaxed">
          A análise representa associações observadas nos registros do sistema e não substitui a avaliação técnica em campo.
        </p>
      </div>
    </ChartCard>
  );
}

export const WIDGETS = [
  // Visão executiva
  { id: "kpis", label: "Indicadores gerais (KPIs)", span: 3, vis: "kpis", section: "visao", C: KpisWidget },
  { id: "niveis_risco", label: "Níveis de risco", span: 3, vis: "niveis_risco", section: "visao", C: RiskLevelsWidget },
  { id: "analise_inteligente", label: "Análise Inteligente", span: 3, vis: "analise_inteligente", section: "inteligencia", C: IntelligentAnalysisWidget },
  { id: "por_mes", label: "Ocorrências por mês", span: 2, vis: "por_mes", section: "ocorrencias", C: MonthWidget },
  { id: "por_categoria", label: "Por categoria", span: 1, vis: "por_categoria", section: "ocorrencias", C: CategoryWidget },
  { id: "por_unidade", label: "Ocorrências por unidade", span: 1, vis: "por_unidade", section: "ocorrencias", C: UnitWidget },
  { id: "por_area", label: "Ocorrências por área", span: 1, vis: "por_area", section: "ocorrencias", C: AreaWidget },
  { id: "por_prioridade", label: "Por prioridade", span: 1, vis: "por_prioridade", section: "ocorrencias", C: PriorityWidget },
  { id: "ranking", label: "Ranking de unidades", span: 1, vis: "ranking", section: "ocorrencias", C: RankingWidget },
  { id: "sla", label: "SLA por cliente", span: 2, vis: "sla", section: "ocorrencias", C: SlaWidget },

  // Controle de roedores
  { id: "roedores_especie", label: "Roedores por espécie", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentSpeciesWidget },
  { id: "roedores_evolucao", label: "Evolução de roedores por espécie", span: 2, vis: "capturas_roedores", section: "roedores", C: RodentEvolutionWidget },
  { id: "roedores_unidade", label: "Roedores por unidade", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentUnitWidget },
  { id: "roedores_area", label: "Roedores por área", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentAreaWidget },
  { id: "roedores_setor", label: "Roedores por setor / sala", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentSectorWidget },
  { id: "roedores_quantidade", label: "Roedores por quantidade", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentQuantityWidget },
  { id: "roedores_situacao", label: "Situação dos roedores", span: 1, vis: "capturas_roedores", section: "roedores", C: RodentSituationWidget },
  { id: "capturas_roedores", label: "Capturas de roedores", span: 2, vis: "capturas_roedores", section: "roedores", C: CapturesWidget },
  { id: "salas_saude", label: "Saúde das salas elétricas", span: 1, vis: "salas_saude", section: "roedores", C: RoomsHealthWidget },

  // Pragas
  { id: "pragas_especie", label: "Pragas por tipo de ocorrência", span: 1, vis: "pragas", section: "pragas", C: PestSpeciesWidget },
  { id: "pragas_evolucao", label: "Evolução por tipo de ocorrência", span: 2, vis: "pragas", section: "pragas", C: PestEvolutionWidget },
  { id: "areas_criticas", label: "Áreas críticas", span: 2, vis: "areas_criticas", section: "pragas", C: CriticalAreasWidget },

  // PGA
  { id: "pga", label: "Pragas em Grãos (PGA)", span: 2, vis: "pga", section: "pga", C: PgaWidget },

  // Operação e ambiente
  { id: "operacional_astral", label: "Não conformidades / Operacional Astral", span: 1, vis: "operacional_astral", section: "ambiental", C: AstralOpsWidget },
  { id: "risco_operacional", label: "Risco Operacional (Ambiental)", span: 2, vis: "risco_operacional", section: "ambiental", C: RiskOpWidget },
];

export const DASHBOARD_SECTIONS = [
  { id: "visao", title: "Visão Executiva", description: "Resumo geral do desempenho operacional e do risco atual." },
  { id: "inteligencia", title: "Inteligência Ambiental", description: "Leituras automáticas para apoiar decisões e priorização." },
  { id: "ocorrencias", title: "Ocorrências e Não Conformidades", description: "Distribuição, criticidade, SLA e concentração das ocorrências." },
  { id: "roedores", title: "Controle de Roedores", description: "Espécies, quantidade, localização e evolução das capturas." },
  { id: "pragas", title: "Controle de Pragas", description: "Tipos de ocorrência, evolução e áreas de maior criticidade." },
  { id: "pga", title: "Pragas de Grãos Armazenados", description: "Indicadores específicos do controle de pragas em grãos." },
  { id: "ambiental", title: "Ambiente e Operação", description: "Condições operacionais, salas elétricas e risco ambiental." },
];

export const SPAN_CLASS = { 1: "", 2: "lg:col-span-2", 3: "lg:col-span-3" };